import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
} from './ui/sheet';
import { Badge } from './ui/badge';
import DepartmentSelectorModal from './DepartmentSelectorModal';
import {
  LayoutDashboard,
  Ticket,
  Inbox,
  UserCog,
  MessageSquareHeart,
  Plus,
  CheckSquare,
  Users,
  Bell,
  Sun,
  Moon,
  Menu,
  LogOut,
  Settings,
  ClipboardList,
  Key,
  BarChart3,
  FileEdit,
  DatabaseBackup,
  CalendarDays,
  BookOpen,
  Star,
  Truck,
  GraduationCap,
  Megaphone
} from 'lucide-react';

const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_request-flow-12/artifacts/nazg9foa_United%20Group%20PNG.png';
const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_request-flow-12/artifacts/ky9isxfd_United%20Group%20White%20logo%20PNG%20%281%29.png';

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, employee, logout, isAdmin, impersonating, stopImpersonating, canView, getAuthHeader } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount, notifications, markAsRead, markAllAsRead } = useNotifications();
  const { currentModule } = useCurrentModule();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [deptModalOpen, setDeptModalOpen] = useState(false);
  const [hasMyInduction, setHasMyInduction] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [hasOwnerSessions, setHasOwnerSessions] = useState(false);
  const [hasUnseenAnnouncements, setHasUnseenAnnouncements] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [myR, apR, anR] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inductions/my`, { headers: getAuthHeader() }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inductions/owner/pending`, { headers: getAuthHeader() }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/announcements/unseen`, { headers: getAuthHeader() }),
        ]);
        if (myR.ok) { const d = await myR.json(); if (active) setHasMyInduction(Array.isArray(d) && d.length > 0); }
        if (anR.ok) { const d = await anR.json(); if (active) setHasUnseenAnnouncements(Array.isArray(d) && d.length > 0); }
        if (apR.ok) {
          const d = await apR.json();
          if (active) {
            const arr = Array.isArray(d) ? d : [];
            // owner/pending already returns only sessions within their active lifecycle
            // (pending/accepted-until-completed, or within the 2-day post-terminal window),
            // so the tab is visible whenever the owner has any such session.
            setHasOwnerSessions(arr.length > 0);
            // Badge counts only sessions that need an action now (respond or confirm conduct).
            const actionable = arr.filter(x => ['Pending', 'Reschedule Requested'].includes(x.approval_status) || x.awaiting_conduct);
            setPendingApprovals(actionable.length);
          }
        }
      } catch (_e) { /* ignore */ }
    })();
    return () => { active = false; };
  }, [getAuthHeader]);

  const logoUrl = theme === 'dark' ? LOGO_DARK : LOGO_LIGHT;
  const headerTitle = currentModule === 'IT' ? 'IT Desk' : 'Admin Desk';

  const role = employee?.role || 'Employee';
  const isSuperAdmin = role === 'Super Admin';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', show: canView('dashboard') },
    { icon: Plus, label: 'Create Ticket', path: '/tickets/create', onClick: () => setDeptModalOpen(true), show: canView('create_ticket') },
    { icon: Ticket, label: 'My Requests', path: '/tickets', show: canView('my_tickets') },
    { icon: Inbox, label: 'Informed Requests', path: '/informed-requests', show: !!(employee?.is_spoc || employee?.is_informed_spoc) },
    { icon: UserCog, label: 'SPOC Management', path: '/spoc-management', show: ['Super Admin', 'Admin Head'].includes(role) },
    { icon: CheckSquare, label: 'Approvals', path: '/approvals', show: canView('approvals') },
    { icon: ClipboardList, label: 'Admin Queue', path: '/admin/queue', show: canView('admin_queue') },
    { icon: ClipboardList, label: 'IT Queue', path: '/it/queue', show: canView('it_queue') },
    { icon: Bell, label: 'Escalated Requests', path: '/it/escalated', show: canView('escalated_requests') },
    { icon: CalendarDays, label: 'View Room Schedule', path: '/room-schedule', show: canView('meeting_room') },
    { icon: BarChart3, label: 'Analytics', path: '/analytics', show: canView('analytics') },
    { icon: BookOpen, label: 'Daily Expenses', path: '/daily-expenses', show: canView('daily_expenses') },
    { icon: Truck, label: 'Courier Details', path: '/courier-details', show: canView('courier_details') },
    { icon: ClipboardList, label: 'Surveys', path: '/surveys', show: canView('surveys') },
    { icon: ClipboardList, label: 'My Surveys', path: '/my-surveys', show: true },
    { icon: GraduationCap, label: 'Induction', path: '/inductions', show: canView('inductions') },
    { icon: CheckSquare, label: 'Induction Approvals', path: '/induction-approvals', show: hasOwnerSessions, badge: pendingApprovals },
    { icon: GraduationCap, label: 'My Induction', path: '/my-induction', show: hasMyInduction },
    { icon: FileEdit, label: 'Expense Masters', path: '/expense-masters', show: canView('expense_masters') },
    { icon: ClipboardList, label: 'Budget Requests', path: '/budget-requests', show: canView('budget_requests') },
    { icon: Key, label: 'Procurement', path: '/procurement', show: canView('procurement') },
    { icon: Star, label: 'Feedback', path: '/feedback', show: canView('feedback') },
    { icon: MessageSquareHeart, label: 'Client Visit Feedback', path: '/client-visit-feedback', show: ['Management', 'Super Admin'].includes(role) },
    { icon: FileEdit, label: 'Request Types', path: '/request-types', show: canView('request_types') },
    { icon: FileEdit, label: 'IT Request Types', path: '/it-request-types', show: canView('it_request_types') },
    { icon: Key, label: 'Location Access', path: '/location-access', show: canView('location_access') },
    { icon: DatabaseBackup, label: 'Backup Management', path: '/backup-management', show: canView('backup_management') && ['adminmum@united-group.in', 'abhishek.p@united-group.in'].includes(employee?.email?.toLowerCase()) },
    { icon: Users, label: 'Employee Details', path: '/employees', show: canView('employee_details') || isAdmin },
    { icon: Megaphone, label: 'Announcements', path: '/announcements', show: true, dot: hasUnseenAnnouncements },
  ];

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    // If item has its own onClick (e.g., open a modal), use button instead of Link
    if (item.onClick) {
      const handleClick = (e) => {
        e.preventDefault();
        item.onClick();
        if (onClick) onClick();
      };
      return (
        <button
          type="button"
          onClick={handleClick}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          className={`
            sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm w-full text-left
            ${isActive 
              ? 'bg-accent text-foreground' 
              : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
            }
          `}
        >
          <item.icon className="w-5 h-5" />
          <span>{item.label}</span>
        </button>
      );
    }
    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`
          sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm
          ${isActive 
            ? 'bg-accent text-foreground' 
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
          }
        `}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
        {item.dot && <span className="ml-auto h-2.5 w-2.5 rounded-full bg-red-600" data-testid="nav-announcements-dot" aria-label="New announcements" />}
        {item.badge > 0 && <span className="ml-auto text-xs font-semibold bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid="nav-badge">{item.badge}</span>}
      </Link>
    );
  };

  const SidebarContent = ({ onItemClick }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-border/40">
        <Link to="/dashboard" className="flex items-center gap-3">
          <img src={logoUrl} alt="United Group" className="h-8 w-auto max-w-[140px] object-contain" />
          <span className="font-bold text-base" data-testid="header-app-title">{headerTitle}</span>
        </Link>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-4 py-6">
        <nav className="space-y-2">
          {navItems.filter(item => item.show).map((item) => (
            <NavLink key={item.path} item={item} onClick={onItemClick} />
          ))}
        </nav>
      </ScrollArea>

      {/* User info */}
      <div className="p-4 border-t border-border/40">
        <div className="flex items-center gap-3 px-2">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.picture} />
            <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">
              {role}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-slate-50/50 dark:bg-slate-900/50 border-r border-border/40 backdrop-blur-xl">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-64">
          <SidebarContent onItemClick={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Impersonation Banner */}
        {impersonating && (
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm z-50" data-testid="impersonation-banner">
            <span className="font-medium">Viewing as: {employee?.full_name} ({employee?.role})</span>
            <button onClick={stopImpersonating} className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs font-medium transition-colors" data-testid="stop-impersonation-btn">
              Switch Back to Super Admin
            </button>
          </div>
        )}
        {/* Top Bar */}
        <header className="h-16 border-b border-border/40 bg-background/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 md:px-6">
          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>

          {/* Page title - hidden on mobile */}
          <div className="hidden md:block" />

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
              className="hover:bg-accent"
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>

            {/* Notifications */}
            <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative hover:bg-accent"
                  data-testid="notifications-btn"
                >
                  <Bell className={`h-5 w-5 ${unreadCount > 0 ? 'bell-animate' : ''}`} />
                  {unreadCount > 0 && (
                    <Badge 
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-destructive text-destructive-foreground"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notifications</span>
                  {unreadCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-auto p-1 text-xs"
                      onClick={markAllAsRead}
                    >
                      Mark all read
                    </Button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <ScrollArea className="h-64">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <DropdownMenuItem
                        key={notif.notification_id}
                        className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${!notif.is_read ? 'bg-accent/50' : ''}`}
                        onClick={() => {
                          markAsRead(notif.notification_id);
                          if (notif.link) {
                            navigate(notif.link);
                            setNotifOpen(false);
                          } else if (notif.ticket_id) {
                            navigate(`/tickets/${notif.ticket_id}`);
                            setNotifOpen(false);
                          }
                        }}
                      >
                        <span className="font-medium text-sm">{notif.title}</span>
                        <span className="text-xs text-muted-foreground line-clamp-2">{notif.message}</span>
                        <span className="text-xs text-muted-foreground/70">
                          {new Date(notif.created_at).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </DropdownMenuItem>
                    ))
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/change-password')}>
                  <Key className="mr-2 h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
      <DepartmentSelectorModal open={deptModalOpen} onOpenChange={setDeptModalOpen} />
    </div>
  );
};
